/**
 * Development utility for testing offline queue functionality.
 * Inject mock queues into the offline registry for testing without actual offline saves.
 * 
 * Usage in React component or browser console:
 * 
 * import { injectMockOfflineQueues, clearMockOfflineQueues } from '@seims/shared';
 * 
 * // Inject 3 mock queues with test data
 * injectMockOfflineQueues(3);
 * 
 * // Or inject with custom items
 * injectMockOfflineQueues(2, { venue: 'test venue' });
 * 
 * // Clear all mocks
 * clearMockOfflineQueues();
 */

import { __DEV_getOfflineQueueRegistry, publishRegistryState } from './useOfflineMutationQueue';

/**
 * Create a mock queue entry
 */
const createMockQueueEntry = (storageKey, itemCount = 1, mockData = {}) => {
  const items = Array.from({ length: itemCount }, (_, i) => ({
    id: `mock-${storageKey}-${i}`,
    payload: {
      id: Math.random().toString(36).substr(2, 9),
      ...mockData,
      timestamp: Date.now(),
    },
    attempts: 0,
    lastError: null,
    lastAttemptAt: null,
    queued: true,
  }));

  return {
    storageKey,
    queueCount: items.length,
    isSyncing: false,
    syncNow: async () => {
      // Simulate sync delay
      await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 1000));
      
      // Randomly fail 0-1 items for testing failed attempts
      const failCount = Math.floor(Math.random() * 2);
      const syncedCount = items.length - failCount;
      
      return {
        total: items.length,
        syncedCount,
        failedCount: failCount,
        remainingCount: 0,
      };
    },
    items,
  };
};

/**
 * Inject mock offline queues into the registry.
 * 
 * @param {number} queueCount - Number of mock queues to inject (default: 1)
 * @param {object} mockData - Sample data to include in each queue item (default: {})
 */
export const injectMockOfflineQueues = (queueCount = 1, mockData = {}) => {
  if (typeof window === "undefined") {
    console.warn("injectMockOfflineQueues: window is undefined, skipping injection");
    return;
  }

  const registry = __DEV_getOfflineQueueRegistry();
  if (!registry) {
    console.error("injectMockOfflineQueues: Failed to get registry");
    return;
  }

  const mockQueues = [
    { key: "offline-venue-queue", label: "Add Venue", items: 2 },
    { key: "offline-event-queue", label: "Create Event", items: 1 },
    { key: "offline-attendee-queue", label: "Add Attendee", items: 3 },
  ];

  // Inject requested number of mock queues
  for (let i = 0; i < Math.min(queueCount, mockQueues.length); i++) {
    const { key, label, items: itemCount } = mockQueues[i];
    const mockEntry = createMockQueueEntry(key, itemCount, {
      ...mockData,
      label,
    });
    registry.set(key, mockEntry);
    console.log(`[Mock] Injected queue: ${key} with ${itemCount} items`);
  }

  // Dispatch registry update event
  publishRegistryState();
  console.log(`[Mock] Registry updated with ${queueCount} mock queues. Total items: ${getTotalMockQueueCount()}`);
};

/**
 * Clear all mock offline queues from the registry.
 */
export const clearMockOfflineQueues = () => {
  if (typeof window === "undefined") {
    return;
  }

  const registry = __DEV_getOfflineQueueRegistry();
  if (!registry) {
    return;
  }

  const count = registry.size;
  registry.clear();
  publishRegistryState();
  console.log(`[Mock] Cleared ${count} mock queues`);
};

/**
 * Get the total count of items across all mock queues
 */
export const getTotalMockQueueCount = () => {
  if (typeof window === "undefined") {
    return 0;
  }

  const registry = __DEV_getOfflineQueueRegistry();
  if (!registry) {
    return 0;
  }

  return Array.from(registry.values()).reduce(
    (acc, entry) => acc + (entry.queueCount || 0),
    0
  );
};

/**
 * Setup mock offline registry for testing.
 * Call this early (in App.jsx or before mounting EventsHeader) to inject mocks.
 */
export const setupMockOfflineRegistry = (queueCount = 1, mockData = {}) => {
  console.log("[Mock] Setting up mock offline registry...");
  injectMockOfflineQueues(queueCount, mockData);
  console.log(
    "[Mock] Mock registry ready. Use clearMockOfflineQueues() to reset."
  );
};

// Expose for browser console testing
if (typeof window !== "undefined") {
  window.__SEIMS_OFFLINE_MOCK = {
    inject: injectMockOfflineQueues,
    clear: clearMockOfflineQueues,
    getTotalCount: getTotalMockQueueCount,
    setup: setupMockOfflineRegistry,
  };
  console.log(
    "[Mock] Offline mock utilities exposed as window.__SEIMS_OFFLINE_MOCK in browser console"
  );
}
